<script setup lang="ts">
import { ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { PasswordRequirements, VButton, VPasswordField } from '@/modules/shared/components'
import { resetPasswordService } from '../api'
import AuthLayout from '../layouts/auth-layout.vue'
import FormError from '../components/form-error.vue'

const router = useRouter()
const route = useRoute()

// state
const loading = ref(false)
const error = ref(null)
const errorAnimated = ref(false)

// form
const password = ref(null)
const passwordRequirementsValid = ref(false)
const passwordSubmitAttempted = ref(false)

// need to resquest a verification code on page load

// submit form, handle states, and redirect
const reset_password = async () => {
  passwordSubmitAttempted.value = true
  // prevent multiple requests
  if (loading.value === true || !passwordRequirementsValid.value) {
    return
  }

  // start
  loading.value = true

  try {
    await resetPasswordService({
      reset_token: (route.query.reset_token as string) || '',
      new_password: password.value,
    })
    router.push({ name: 'login' })
  } catch (e) {
    error.value = e.message
    errorAnimated.value = false
    setTimeout(() => (errorAnimated.value = true), 820)
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <AuthLayout>
    <template v-slot:icon>
      <svg width="90" height="85" viewBox="0 0 90 85" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M70.5737 40.695L61.4537 31.575L69.9337 23.087L79.0137 32.167C81.3737 34.447 85.1737 34.367 87.4937 32.007C89.7337 29.647 89.7337 25.967 87.4937 23.647L78.3737 14.527L82.5977 10.295C84.9177 7.93503 84.9177 4.13503 82.5977 1.77503C80.2377 -0.584968 76.4377 -0.584968 74.0777 1.73503L31.0137 44.775V44.771C20.7737 39.251 8.05368 43.051 2.53368 53.251C-2.98632 63.451 0.813678 76.171 11.0137 81.691C21.2137 87.171 33.9337 83.371 39.4537 73.171C42.8137 66.931 42.8137 59.411 39.4137 53.171L52.7737 39.811L61.8537 48.931C64.1737 51.251 67.9737 51.251 70.3337 48.891C72.6537 46.531 72.6537 42.731 70.2937 40.371L70.5737 40.695Z"
          fill="#111827"
        />
        <path
          d="M21.1976 55.855C19.2085 55.855 17.3009 56.6452 15.8943 58.0517C14.4878 59.4582 13.6976 61.3659 13.6976 63.355C13.6976 65.3441 14.4878 67.2518 15.8943 68.6583C17.3009 70.0648 19.2085 70.855 21.1976 70.855C23.1868 70.855 25.0944 70.0648 26.5009 68.6583C27.9075 67.2518 28.6976 65.3441 28.6976 63.355C28.6976 61.3659 27.9075 59.4582 26.5009 58.0517C25.0944 56.6452 23.1868 55.855 21.1976 55.855Z"
          stroke="white"
          stroke-width="6"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </template>

    <div class="w-full sm:w-[380px]">
      <header>
        <h1 class="text-3xl leading-9 text-gray-900">Reset your password</h1>
        <p class="mt-6 text-gray-700">Please enter a new password.</p>
      </header>
      <main class="animate sm:mt-18 mt-12" :class="{ 'animate-shake': errorAnimated === false && error !== null }">
        <VPasswordField
          v-model="password"
          label="Password"
          placeholder="••••••••••••"
          @keydown.enter="reset_password"
        />
        <PasswordRequirements
          :password="password"
          :show-errors="passwordSubmitAttempted"
          @update:valid="passwordRequirementsValid = $event"
        />
        <FormError :error="error" v-show="error !== null" />
        <VButton class="mt-12 w-full" :loading="loading" size="xl" variant="primary" @click="reset_password">
          Continue
        </VButton>
      </main>
      <footer class="mt-12 text-center">
        <RouterLink class="text-xs text-gray-500 underline" :to="{ name: 'login' }">Return to login</RouterLink>
      </footer>
    </div>
  </AuthLayout>
</template>
